import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ADD_ADDITIONAL_COST, DELETE_ADDITIONAL_COST, UPDATE_ADDITIONAL_COST } from '@queries/additional-costs.graphql';
import {
  ADD_PROPOSAL,
  DELETE_PROPOSAL,
  GET_ALL_PROPOSALS,
  GET_PROPOSAL_HISTORY,
  GET_PROPOSALS_BY_ID,
  GET_PROPOSALS_TRASH,
  PROPOSAL_STATUS,
  RESTORE_PROPOSAL,
  SEND_PROPOSAL,
  PROPOSAL_EMAIL_MODE,
  UP_PROPOSAL,
} from '@queries/proposals.graphql';
import {
  PROPOSAL_LINE_REFRESH_CAPACITY,
  PROPOSAL_LINE_ASSIGN_FREIGHT_CONTAINER_TYPE,
  PROPOSAL_LINE_SET_CONTAINER_TYPE,
  PROPOSAL_LINE_SET_CONTAINERS,
  PROPOSAL_LINE_SET_QUANTITY,
  PROPOSAL_LINE_SET_TRANSPORT_MODE,
  PROPOSAL_LINE_SET_VOLUME,
} from '@queries/proposals.graphql';
import { Apollo } from 'apollo-angular';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, ReplaySubject, switchMap } from 'rxjs';

import {
  AdditionalCostType,
  CostType,
  COST_TYPE_FROM_BACKEND,
  COST_TYPE_TO_BACKEND,
} from './proposal.types';

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  private _item$: BehaviorSubject<any> = new BehaviorSubject(null);
  private _items$: BehaviorSubject<any> = new BehaviorSubject(null);

  private _item: any;
  private _items: any;

  constructor(
    private apollo: Apollo,
    private http: HttpClient,
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get proposal$(): Observable<any> {
    return this._item$.asObservable();
  }

  get proposals$(): Observable<any[]> {
    return this._items$.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  getList(params: {
    where?: any;
    orderBy?: any;
    skip?: number;
    take?: number;
  }): Observable<any[]> {
    return (
      this.apollo
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .watchQuery<any>({
          query: GET_ALL_PROPOSALS,
          variables: {
            where: params.where,
            orderBy: params.orderBy,
            skip: params.skip,
            take: params.take,
          },
          fetchPolicy: 'cache-and-network',
        })
        .valueChanges.pipe(
          switchMap(({ data }) => {
            const freightTypes = data.ProposalFindAll;
            this._items = freightTypes;
            this._items$.next(this._items);
            return of(this._items);
          }),
        )
    );
  }

  getById(id: number): Observable<any> {
    this._products = [];
    this._products$.next(this._products);

    if (!id) {
      this._item = [];
      this._item$.next(this._item);
      return of(this._item);
    }
    return this.apollo
      .watchQuery<any>({
        query: GET_PROPOSALS_BY_ID,
        variables: {
          where: {
            id,
          },
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          const department = data.ProposalFindOne;
          this._item = department;
          this._item$.next(this._item);
          return of(this._item);
        }),
      );
  }
  getByIdToGetProducts(id: number): Observable<any> {
    return this.apollo
      .watchQuery<any>({
        query: GET_PROPOSALS_BY_ID,
        variables: {
          where: {
            id: +id,
          },
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          const department = data.ProposalFindOne;
          this._item = department;
          return of(department);
        }),
      );
  }

  create(result) {
    return this.apollo
      .mutate<any>({
        mutation: ADD_PROPOSAL,
        variables: {
          data: {
            reference: result,
          },
        },
      })
      .pipe(
        switchMap(({ data }) => {
          const department = data.ProposalCreate;
          return of(department);
        }),
      );
  }

  update(id: number, data: any) {
    return this.apollo
      .mutate<any>({
        mutation: UP_PROPOSAL,
        variables: {
          where: {
            id: +id,
          },
          data,
        },
      })
      .pipe(
        switchMap(({ data: response }) => {
          const proposal = response.ProposalUpdate;
          return of(proposal);
        }),
      );
  }

  delete(id: number) {
    return this.apollo
      .mutate<any>({
        mutation: DELETE_PROPOSAL,
        variables: {
          where: {
            id: +id,
          },
        },
      })
      .pipe(
        switchMap(({ data }) => {
          return of(!!data?.ProposalDelete);
        }),
      );
  }

  getTrash(params: {
    where?: any;
    orderBy?: any;
    skip?: number;
    take?: number;
  }): Observable<any[]> {
    return (
      this.apollo
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .watchQuery<any>({
          query: GET_PROPOSALS_TRASH,
          variables: {
            where: params.where,
            orderBy: params.orderBy,
            skip: params.skip,
            take: params.take,
          },
          fetchPolicy: 'network-only',
        })
        .valueChanges.pipe(
          switchMap(({ data }) => {
            const deletedProposals = data.ProposalTrash;
            this._items = deletedProposals;
            this._items$.next(this._items);
            return of(this._items);
          }),
        )
    );
  }

  restore(id: number) {
    return this.apollo
      .mutate<any>({
        mutation: RESTORE_PROPOSAL,
        variables: {
          where: {
            id: +id,
          },
        },
      })
      .pipe(
        switchMap(({ data }) => {
          return of(!!data?.ProposalRestore);
        }),
      );
  }

  statusFindAll() {
    return this.apollo
      .watchQuery<any>({
        query: PROPOSAL_STATUS,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          const status = data.ProposalStatusFindAll;
          console.log('🚀 ~ ProposalService ~ switchMap ~ status:', status);
          return of(status);
        }),
      );
  }

  downloadPdf(id: number): Observable<Blob> {
    return this.http.get(`${environment.api}proposals/${id}/pdf`, {
      responseType: 'blob',
    });
  }

  sendProposal(
    id: number,
    target: { clientIds: number[] } | { internalEmail: string },
  ): Observable<{ successCount: number; failures: any[] }> {
    return this.apollo
      .mutate<any>({
        mutation: SEND_PROPOSAL,
        variables: {
          // GraphQL ID fields reach the browser as strings ("39") but ProposalSend declares $id: Int!.
          id: +id,
          clientIds: 'clientIds' in target ? target.clientIds : undefined,
          internalEmail: 'internalEmail' in target ? target.internalEmail : undefined,
        },
      })
      .pipe(
        switchMap(({ data }) => of(data.ProposalSend)),
      );
  }

  /** Whether proposal emails go to the real recipients (live) or to the backend test recipient. */
  getEmailMode(): Observable<{ live: boolean; testRecipient: string | null }> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .query<{ ProposalEmailMode: { live: boolean; testRecipient: string | null } }>({
        query: PROPOSAL_EMAIL_MODE,
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => data.ProposalEmailMode));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ CART methods
  // -----------------------------------------------------------------------------------------------------

  public _shipping$: ReplaySubject<any> = new ReplaySubject(1);
  public _shipping: any = [];
  public _products$: ReplaySubject<any> = new ReplaySubject(1);
  public _products: any = [];

  /**
   * Getter for pagination
   */
  get shipping$(): Observable<any> {
    return this._shipping$.asObservable();
  }

  /**
   * Getter for pagination
   */
  get products$(): Observable<any> {
    return this._products$.asObservable();
  }

  /**
   * Setter for pagination
   */
  set shipping(value) {
    const index = this._shipping.findIndex(
      (item: any) => item.id === value.id,
    );
    if (index > -1) {
      this._shipping[index] = value;
    } else {
      this._shipping.push(value);
    }

    this._shipping$.next([...this._shipping]);
  }

  set products(value) {
    const normalized = value?.additionalCosts
      ? {
          ...value,
          additionalCosts: value.additionalCosts.map((cost: any) => ({
            ...cost,
            type: COST_TYPE_FROM_BACKEND[cost.type as AdditionalCostType] ?? cost.type,
          })),
        }
      : value;

    const index = this._products.findIndex(
      (item: any) => item.id === normalized.id,
    );
    if (index > -1) {
      this._products[index] = normalized;
    } else {
      this._products.push(normalized);
    }

    this._products$.next([...this._products]);
  }

  addProductToProposal(proposalId: number, args: any) {
    const product = args.product;
    const pricingObject = product.Pricings?.find((p) => p.isMainCurrency);
    const total = product.salePrice ?? (() => {
      const price = +(pricingObject?.value ?? 0);
      const profitMargin = +product.profitMargin || 0;
      return price + price * (profitMargin / 100);
    })();
    return this.apollo
      .mutate<any>({
        mutation: UP_PROPOSAL,
        variables: {
          where: {
            id: +proposalId,
          },
          data: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ProductOffer2Proposal: {
              create: [
                {
                  product: {
                    connect: {
                      id: +product.id,
                      number: product.reference,
                    },
                  },
                  inconTerm: {
                    connect: {
                      id: +product.InconTerm.id,
                    },
                  },
                  total: +total,
                },
              ],
            },
          },
        },
      })
      .pipe(
        switchMap(({ data }) => {
          const department = data.ProposalUpdate;
          return of(department);
        }),
      );
  }

  removeProductFromProposal(proposalId: number, productId: number) {
    return this.apollo
      .mutate<any>({
        mutation: UP_PROPOSAL,
        variables: {
          where: {
            id: +proposalId,
          },
          data: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ProductOffer2Proposal: {
              delete: [
                {
                  proposalId_productId: {
                    proposalId: +proposalId,
                    productId: +productId,
                  },
                },
              ],
            },
          },
        },
      })
      .pipe(
        switchMap(({ data }) => {
          const department = data.ProposalUpdate;
          this._products = this._products.filter(
            (item: any) => item.product?.id !== productId,
          );
          this._products$.next(this._products);
          return of(department);
        }),
      );
  }

  addAdditionalCostToOffer(
    productOffer2ProposalId: number,
    cost: {
      type: CostType;
      description: string;
      // Optional: FRETE on container lines is priced by the backend from the freight basis (D10), so the
      // frontend never sends a computed value for it (PRD 6.10 #17).
      value?: number;
      // SEGURO, DESALFANDEGAMENTO, TRANSPORTE, OUTRO: the TOTAL of the cost for the line. The backend stores it and
      // derives the per m3 value (total / line volume), so it is sent instead of `value`.
      totalAmount?: number;
      supplierReference?: string;
      freightId?: number;
    },
  ) {
    const data: any = {
      type: COST_TYPE_TO_BACKEND[cost.type],
      description: cost.description,
      productOffer2Proposal: { connect: { id: +productOffer2ProposalId } },
    };
    if (cost.value !== undefined) {
      data.value = cost.value;
    }
    if (cost.supplierReference) {
      data.supplierReference = cost.supplierReference;
    }
    if (cost.freightId) {
      data.freight = { connect: { id: +cost.freightId } };
    }

    return this.apollo
      .mutate<any>({
        mutation: ADD_ADDITIONAL_COST,
        variables: cost.totalAmount !== undefined ? { data, totalAmount: cost.totalAmount } : { data },
      })
      .pipe(
        switchMap(({ data: response }) => of(response.AdditionalCostCreate)),
      );
  }

  /** "Alterar frete": replaces the freight of an existing FRETE cost in ONE call (the backend re-derives basis and value). */
  updateAdditionalCostFreight(costId: number, freightId: number) {
    return this.apollo
      .mutate<any>({
        mutation: UPDATE_ADDITIONAL_COST,
        variables: {
          where: { id: +costId },
          data: { freight: { connect: { id: +freightId } } },
        },
      })
      .pipe(
        switchMap(({ data: response }) => of(response.AdditionalCostUpdate)),
      );
  }

  removeAdditionalCostFromOffer(additionalCostId: number) {
    return this.apollo
      .mutate<any>({
        mutation: DELETE_ADDITIONAL_COST,
        variables: { where: { id: +additionalCostId } },
      })
      .pipe(
        switchMap(({ data }) => of(!!data?.AdditionalCostDelete)),
      );
  }

  /**
   * D16: rewrites the container counts of ONE proposal line (the offer itself is untouched). Only type ids and
   * counts travel to the backend: capacity, volume, freight re-spread and totals are all computed there, and the
   * caller refreshes the products cache from the server afterwards.
   */
  setLineContainers(lineId: number, containers: { quantityTypeId: number; count: number }[]) {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_SET_CONTAINERS,
        variables: {
          lineId: +lineId,
          containers: containers.map((c) => ({ quantityTypeId: +c.quantityTypeId, count: +c.count })),
        },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineSetContainers)));
  }

  /** D16 for NON container lines: the m3 quantity of this proposal line only. */
  setLineQuantity(lineId: number, quantity: number) {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_SET_QUANTITY,
        variables: { lineId: +lineId, quantity: +quantity },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineSetQuantity)));
  }

  /**
   * Ships ONE proposal line in containers or in bulk. Only the line id and the mode travel: the backend clears the
   * container snapshot and the freight of the old mode, re-derives the volume and the totals, and the caller refreshes the
   * products cache afterwards.
   */
  setLineTransportMode(lineId: number, mode: 'CONTAINER' | 'BULK') {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_SET_TRANSPORT_MODE,
        variables: { lineId: +lineId, mode },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineSetTransportMode)));
  }

  /**
   * D19: edits the requested volume (m3) of ONE proposal line that has a single container type. Only the line id and the
   * volume the user typed travel to the backend: the container count (ceil(volume / capacity)), the freight re-spread and
   * the totals are computed there, and the caller refreshes the products cache from the server afterwards.
   */
  setLineVolume(lineId: number, volumeM3: number) {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_SET_VOLUME,
        variables: { lineId: +lineId, volumeM3: +volumeM3 },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineSetVolume)));
  }

  /**
   * D20: puts a legacy m3 offer line in containers of the chosen type. Only the two ids travel to the backend: the volume
   * of the line becomes its requested volume and the container count is computed there.
   */
  setLineContainerType(lineId: number, quantityTypeId: number) {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_SET_CONTAINER_TYPE,
        variables: { lineId: +lineId, quantityTypeId: +quantityTypeId },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineSetContainerType)));
  }

  /**
   * Gives a pending FRETE cost (freight type without container type) its container type. On a legacy line the backend also
   * converts the line to containers of that type; on a container line the type must be in the snapshot. An optional
   * capacityM3 (m3 per container) is a line-only capacity typed by the user: the species grid is not changed.
   */
  assignFreightContainerType(costId: number, quantityTypeId: number, capacityM3?: number | null) {
    const variables: Record<string, number> = { costId: +costId, quantityTypeId: +quantityTypeId };
    if (capacityM3 != null) {
      variables.capacityM3 = +capacityM3;
    }
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_ASSIGN_FREIGHT_CONTAINER_TYPE,
        variables,
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineAssignFreightContainerType)));
  }

  /** Resets the line snapshot to the offer containers and discards any quantity override. */
  refreshLineCapacity(lineId: number) {
    return this.apollo
      .mutate<any>({
        mutation: PROPOSAL_LINE_REFRESH_CAPACITY,
        variables: { lineId: +lineId },
      })
      .pipe(switchMap(({ data }) => of(data.ProposalLineRefreshCapacity)));
  }

  getHistory(proposalId: number): Observable<any[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ ProposalHistoryFindMany: any[] }>({
        query: GET_PROPOSAL_HISTORY,
        // GraphQL ID fields reach the browser as strings ("39") but ProposalHistoryFindMany declares
        // $proposalId: Int!, so the id is always coerced (dev error: Int cannot represent non-integer value).
        variables: { proposalId: +proposalId },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => of(data.ProposalHistoryFindMany)),
      );
  }
}
